// Pure search code — no DOM, no Vite-only imports. Runs in main thread or
// in a Web Worker.
import type { CompactIndex, JsonSearchIndex, SearchHit } from "./types";

// ── Tokenization ────────────────────────────────────────────────────────────

const HE_NIKKUD = /[֑-ׇ]/g;
const HE_FINAL: Record<string, string> = {
  "ך": "כ", "ם": "מ", "ן": "נ", "ף": "פ", "ץ": "צ",
};

const EN_TOKEN = /[a-z0-9']+/g;
const HE_TOKEN = /[א-ת]+/g;

const EN_STOPWORDS = new Set([
  "a","an","and","the","of","to","in","is","it","that","for","on","with","as",
  "by","this","be","are","was","were","but","not","or","at","from","he","she",
  "his","her","they","their","them","you","your","i","we","our","us","my","me",
  "do","did","does","so","if","then","than","there","these","those","such",
  "shall","will","would","should","can","could","may","might","one","also","its",
  "into","upon","unto","which","who","whom","what","when","where","how","why",
  "have","has","had","been","being","am","done","each","other","more",
]);

const HE_PREFIXES = [
  "וכשה","ובכש","ושכש",
  "וכש","ובכ","ובש","ולש","ומש","ומה","וכה","ושה","וכל","ובל",
  "כש","וכ","וב","ול","ומ","וש","וה","שה","שב","של","שמ","בה","לה","מה","כה",
  "ה","ו","ב","כ","ל","מ","ש",
];

const HE_SUFFIXES = ["ים","ות","תי","תו","תה","תם","תן","כם","הם","ה","ת","ם","ן","י","ו","ך"];

export function normalizeHe(tok: string): string {
  let s = tok.replace(HE_NIKKUD, "");
  let out = "";
  for (const ch of s) out += HE_FINAL[ch] ?? ch;
  return out;
}

export function tokenizeEn(text: string): string[] {
  const out: string[] = [];
  for (const m of text.toLowerCase().matchAll(EN_TOKEN)) {
    const t = m[0].replace(/^'+|'+$/g, "");
    if (t.length < 2 || EN_STOPWORDS.has(t)) continue;
    out.push(t);
  }
  return out;
}

export function tokenizeHe(text: string): string[] {
  const out: string[] = [];
  for (const m of text.normalize("NFC").matchAll(HE_TOKEN)) {
    const t = normalizeHe(m[0]);
    if (t.length < 2) continue;
    out.push(t);
  }
  return out;
}

export function heVariants(token: string): string[] {
  const out = new Set<string>([token]);
  const stripPrefix = (s: string) => {
    for (const p of HE_PREFIXES) {
      if (s.length - p.length >= 3 && s.startsWith(p)) out.add(s.slice(p.length));
    }
  };
  const stripSuffix = (s: string) => {
    for (const sf of HE_SUFFIXES) {
      if (s.length - sf.length >= 3 && s.endsWith(sf)) out.add(s.slice(0, -sf.length));
    }
  };
  stripPrefix(token);
  for (const v of [...out]) stripSuffix(v);
  for (const v of [...out]) {
    if (v.length < 2) continue;
    for (const p of ["ה","ו","ב","כ","ל","מ","ש","וה","וב","ול","ומ","וש"]) {
      out.add(p + v);
    }
  }
  return [...out];
}

// ── JSON → CompactIndex conversion ──────────────────────────────────────────

export function compact(idx: JsonSearchIndex): CompactIndex {
  const totalPairs = idx.postings.reduce((n, p) => n + p.length, 0);
  const data = new Uint32Array(totalPairs);
  const offsets = new Uint32Array(idx.postings.length + 1);
  let pos = 0;
  for (let i = 0; i < idx.postings.length; i++) {
    offsets[i] = pos;
    const p = idx.postings[i];
    for (let j = 0; j < p.length; j++) data[pos++] = p[j];
  }
  offsets[idx.postings.length] = pos;
  return {
    lang: idx.lang,
    n_docs: idx.n_docs,
    n_text_docs: idx.n_text_docs,
    avg_dl: idx.avg_dl,
    vocab: idx.vocab,
    df: Uint32Array.from(idx.df),
    postingsData: data,
    postingsOffsets: offsets,
    dl: Int32Array.from(idx.dl),
  };
}

// ── Vocab lookup ─────────────────────────────────────────────────────────────

function bsearch(vocab: string[], term: string): number {
  let lo = 0, hi = vocab.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const v = vocab[mid];
    if (v === term) return mid;
    if (v < term) lo = mid + 1; else hi = mid - 1;
  }
  return -1;
}

function prefixMatches(vocab: string[], prefix: string, cap = 64): number[] {
  let lo = 0, hi = vocab.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (vocab[mid] < prefix) lo = mid + 1; else hi = mid;
  }
  const out: number[] = [];
  for (let i = lo; i < vocab.length && out.length < cap; i++) {
    if (!vocab[i].startsWith(prefix)) break;
    out.push(i);
  }
  return out;
}

// ── BM25 ─────────────────────────────────────────────────────────────────────

interface QueryTerm {
  surface: string;
  termIdxs: number[];
}

function buildQuery(idx: CompactIndex, raw: string): QueryTerm[] {
  const terms: QueryTerm[] = [];
  if (idx.lang === "en") {
    for (const t of tokenizeEn(raw)) {
      const ti = bsearch(idx.vocab, t);
      if (ti >= 0) {
        terms.push({ surface: t, termIdxs: [ti] });
      } else {
        const pre = prefixMatches(idx.vocab, t, 8);
        if (pre.length) terms.push({ surface: t, termIdxs: pre });
      }
    }
  } else {
    for (const t of tokenizeHe(raw)) {
      const variants = heVariants(t);
      const seen = new Set<number>();
      for (const v of variants) {
        const ti = bsearch(idx.vocab, v);
        if (ti >= 0) seen.add(ti);
        if (v.length >= 3) for (const x of prefixMatches(idx.vocab, v, 16)) seen.add(x);
      }
      if (seen.size) terms.push({ surface: t, termIdxs: [...seen] });
    }
  }
  return terms;
}

const K1 = 1.2;
const B = 0.75;

export function search(idx: CompactIndex, raw: string, limit = 50): SearchHit[] {
  const q = buildQuery(idx, raw);
  if (q.length === 0) return [];
  const scores = new Map<number, number>();
  const matchedByDoc = new Map<number, Set<string>>();
  const totalDocs = idx.n_text_docs || 1;
  const avgDl = idx.avg_dl || 1;

  for (const term of q) {
    let df = 0;
    for (const ti of term.termIdxs) df += idx.df[ti];
    if (df === 0) continue;
    const idf = Math.log(1 + (totalDocs - df + 0.5) / (df + 0.5));

    const docTf = new Map<number, number>();
    for (const ti of term.termIdxs) {
      const start = idx.postingsOffsets[ti];
      const end = idx.postingsOffsets[ti + 1];
      for (let j = start; j < end; j += 2) {
        const id = idx.postingsData[j];
        const tf = idx.postingsData[j + 1];
        docTf.set(id, (docTf.get(id) ?? 0) + tf);
      }
    }
    for (const [id, tf] of docTf) {
      const dl = idx.dl[id] || 1;
      const norm = tf * (K1 + 1) / (tf + K1 * (1 - B + (B * dl) / avgDl));
      const s = idf * norm;
      scores.set(id, (scores.get(id) ?? 0) + s);
      let m = matchedByDoc.get(id);
      if (!m) { m = new Set(); matchedByDoc.set(id, m); }
      m.add(term.surface);
    }
  }

  const ranked = [...scores.entries()].sort((a, b) => b[1] - a[1]).slice(0, limit);
  return ranked.map(([id, score]) => ({
    id,
    score,
    ref: "",
    work: "",
    text: "",
    matched: [...(matchedByDoc.get(id) ?? new Set())],
  }));
}
